import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { type ConnectedProps, connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { logOut } from "@/actions/auth";
import {
  clearSavedCity,
  clearSavedWhitelabel,
  getCityWhitelabelMappings,
  selectCity,
  selectCityFullInfo,
  selectWhitelabel,
  selectWhitelabelFullInfo,
} from "@/actions/cities";
import {
  setMapBikeStateFilter,
  setMapStationStateFilter,
  setMapStationTypeFilter,
  setMapTypeFilter,
} from "@/actions/screen";
import {
  bikesStates,
  itemsTypes,
  stationStates,
  stationTypes,
} from "@/constants/const";
import { SETTINGS_ROUTE } from "@/constants/routes";
import bigissueLogo from "@/images/bigissueIcon.png";
import esbLogo from "@/images/esbIcon.png";
import farteLogo from "@/images/farteIcon.png";
import gjovikLogo from "@/images/gjovikIcon.png";
import lisaLogo from "@/images/lisaIcon.png";
import sefaLogo from "@/images/sefaIcon.png";
import sigoLogo from "@/images/sigoIcon.png";
import weelzLogo from "@/images/weelzIcon.png";
import { logoutFromFirebase } from "@/utils/firebase";
import { getWhiteLabelLogo, isSuperAdmin } from "@/utils/utils";
import {
  useEffectiveWhitelabelId,
  usePartnerAccessEnabled,
  usePreviewMode,
  useTheme,
} from "@/v2/hooks";
import { usePartners } from "@/v2/queries/partners";
import { useCityStore, useSavedPartnerId } from "@/v2/stores";
import {
  type City,
  Navbar,
  type Partner,
  type Whitelabel,
} from "@/v2/ui/composed/navbar";

/** Extended whitelabel type with cities for mapping */
interface WhitelabelWithCities extends Whitelabel {
  cities?: City[];
}

/** Whitelabel list item with icon for dropdown display */
interface WhitelabelListItem {
  id: number;
  name: string;
  icon?: string;
}

interface AdminDetails {
  cityId?: number | null;
  email?: string;
  whiteLabelCode?: string;
  [key: string]: unknown;
}

interface RootState {
  auth: {
    adminDetails: AdminDetails | null;
  };
  cities: {
    citiesList: City[] | null;
    savedCity: string | null;
    cityWhitelabelMapping: WhitelabelWithCities[] | null;
    savedWhitelabelFullInfo: Whitelabel | null;
  };
}

const WHITELABEL_ICONS: Record<string, string> = {
  sharebike: lisaLogo,
  lisa: lisaLogo,
  sefa: sefaLogo,
  bigissue: bigissueLogo,
  "big issue": bigissueLogo,
  esb: esbLogo,
  fartebysykkel: farteLogo,
  "farte bysykkel": farteLogo,
  gjovik: gjovikLogo,
  gjøvik: gjovikLogo,
  "gjovik bysykkel": gjovikLogo,
  "gjøvik bysykkel": gjovikLogo,
  sigo: sigoLogo,
  weelz: weelzLogo,
  "lisa demo": lisaLogo,
  "lisa-demo": lisaLogo,
};

const mapStateToProps = ({
  auth: { adminDetails },
  cities: {
    citiesList,
    savedCity,
    cityWhitelabelMapping,
    savedWhitelabelFullInfo,
  },
}: RootState) => ({
  adminDetails,
  citiesList,
  savedCity,
  cityWhitelabelMapping,
  savedWhitelabelFullInfo,
});

const mapDispatchToProps = {
  logOut,
  setMapTypeFilter,
  setMapBikeStateFilter,
  setMapStationTypeFilter,
  setMapStationStateFilter,
  selectCity,
  clearSavedCity,
  selectCityFullInfo,
  selectWhitelabel,
  selectWhitelabelFullInfo,
  clearSavedWhitelabel,
  getCityWhitelabelMappings,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface HeaderProps extends PropsFromRedux {
  onMenuClick?: () => void;
  onVersionToggle?: () => void;
  versionLabel?: string;
  onTourClick?: () => void;
}

const Header: React.FC<HeaderProps> = (props) => {
  const {
    adminDetails,
    logOut,
    setMapTypeFilter,
    setMapBikeStateFilter,
    setMapStationTypeFilter,
    setMapStationStateFilter,
    citiesList,
    savedCity,
    clearSavedCity,
    cityWhitelabelMapping,
    savedWhitelabelFullInfo,
    onMenuClick,
    onVersionToggle,
    versionLabel,
    onTourClick,
  } = props;

  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [selectedWhitelabel, setSelectedWhitelabel] =
    useState<Whitelabel | null>(null);
  const [logo, setLogo] = useState<string | null>(null);
  const cityOptions = useMemo(
    () => (Array.isArray(citiesList) ? citiesList : []),
    [citiesList]
  );

  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { effectiveIsCityAdmin, effectiveIsSuperAdmin } = usePreviewMode();

  const partnerAccessEnabled = usePartnerAccessEnabled();
  const effectiveWhitelabelId = useEffectiveWhitelabelId();
  // Partners are scoped per whitelabel; skip the fetch entirely when the
  // partnerAccess flag is off so non-Sigo whitelabels never hit the endpoint.
  const { data: partnersData } = usePartners(
    partnerAccessEnabled ? effectiveWhitelabelId : null
  );
  const partners = useMemo<Partner[]>(
    () => (partnersData ?? []).map((p) => ({ id: p.id, name: p.name })),
    [partnersData]
  );
  const savedPartnerId = useSavedPartnerId();
  const selectPartnerId = useCityStore((s) => s.selectPartnerId);
  const selectedPartner = useMemo<Partner | null>(() => {
    if (savedPartnerId == null) return null;
    return partners.find((p) => p.id === savedPartnerId) ?? null;
  }, [partners, savedPartnerId]);

  useEffect(() => {
    let effectiveWhitelabel = adminDetails?.whiteLabelCode;
    if (isSuperAdmin(adminDetails)) {
      try {
        const stored = localStorage.getItem("whitelabelName");
        if (stored) {
          effectiveWhitelabel = JSON.parse(stored);
        }
      } catch {
        // Ignore parse errors
      }
    }
    setLogo(getWhiteLabelLogo(effectiveWhitelabel));
  }, [adminDetails]);

  useEffect(() => {
    if (savedCity && cityOptions.length > 0) {
      cityOptions.forEach((city) => {
        if (city.id === parseInt(savedCity.split("cityId=")[1])) {
          setSelectedCity(city);
        }
      });
    }
  }, [savedCity, cityOptions]);

  useEffect(() => {
    if (savedWhitelabelFullInfo && !selectedWhitelabel) {
      setSelectedWhitelabel(savedWhitelabelFullInfo);
    }
  }, [savedWhitelabelFullInfo, selectedWhitelabel]);

  useEffect(() => {
    if (effectiveIsSuperAdmin && !cityWhitelabelMapping) {
      // Defer API call to not block initial render
      const timeoutId = setTimeout(() => {
        props.getCityWhitelabelMappings();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [effectiveIsSuperAdmin, cityWhitelabelMapping, props]);

  const whitelabelList = useMemo<WhitelabelListItem[]>(() => {
    const excludedWhitelabels = ["moby", "moby free now"];
    const mappings = Array.isArray(cityWhitelabelMapping)
      ? cityWhitelabelMapping
      : [];
    return mappings
      .filter((w) => !excludedWhitelabels.includes(w.name?.toLowerCase()))
      .map((w) => {
        const nameLower = w.name?.toLowerCase();
        const icon =
          WHITELABEL_ICONS[w.name] || WHITELABEL_ICONS[nameLower] || undefined;
        return { id: w.id, name: w.name, icon };
      });
  }, [cityWhitelabelMapping]);

  const filteredCities = useMemo<City[]>(() => {
    if (!selectedWhitelabel || !Array.isArray(cityWhitelabelMapping)) {
      return cityOptions;
    }
    const whitelabelData = cityWhitelabelMapping.find(
      (w) => w.id === selectedWhitelabel.id
    );
    if (!whitelabelData?.cities) {
      return cityOptions;
    }
    const whitelabelCityIds = new Set(whitelabelData.cities.map((c) => c.id));
    return cityOptions.filter((city) => whitelabelCityIds.has(city.id));
  }, [selectedWhitelabel, cityWhitelabelMapping, cityOptions]);

  const handleWhitelabelChange = useCallback(
    (whitelabel: Whitelabel | null) => {
      if (!whitelabel) {
        props.clearSavedWhitelabel();
      } else {
        props.selectWhitelabel(whitelabel.id);
        props.selectWhitelabelFullInfo(whitelabel);
      }
      props.selectCity("");
      props.selectCityFullInfo("");
      selectPartnerId(null);
      setSelectedCity(null);
      setSelectedWhitelabel(whitelabel);
    },
    [props, selectPartnerId]
  );

  const handlePartnerChange = useCallback(
    (partner: Partner | null) => {
      selectPartnerId(partner ? partner.id : null);
    },
    [selectPartnerId]
  );

  const handleCityChange = useCallback(
    (city: City | null) => {
      if (!city) {
        props.selectCityFullInfo("");
        props.selectCity("");
      } else {
        props.selectCityFullInfo(city);
        props.selectCity(`cityId=${city.id}`);
      }
      setSelectedCity(city);
    },
    [props]
  );

  const logoutAction = useCallback(() => {
    setMapTypeFilter(itemsTypes);
    setMapBikeStateFilter(bikesStates);
    setMapStationTypeFilter(stationTypes);
    setMapStationStateFilter(stationStates);
    clearSavedCity();
    localStorage.removeItem("auth-store");
    sessionStorage.removeItem("preferV1UI");
    logOut();
    logoutFromFirebase();
  }, [
    setMapTypeFilter,
    setMapBikeStateFilter,
    setMapStationTypeFilter,
    setMapStationStateFilter,
    clearSavedCity,
    logOut,
  ]);

  const handleSettingsClick = useCallback(() => {
    const isV2 = location.pathname.startsWith("/v2");
    navigate(isV2 ? "/v2" + SETTINGS_ROUTE : SETTINGS_ROUTE);
  }, [navigate, location.pathname]);

  const handleLogoClick = useCallback(() => {
    const isV2 = location.pathname.startsWith("/v2");
    const targetRoute = isV2 ? "/v2/dashboard" : "/dashboard";
    navigate(targetRoute + location.search);
  }, [navigate, location.pathname, location.search]);

  const handleHelpClick = useCallback(() => {
    if (
      (
        window as Window & {
          zE?: (widget: string, command: string, callback?: () => void) => void;
        }
      ).zE
    ) {
      const zE = (
        window as Window & {
          zE: (widget: string, command: string, callback?: () => void) => void;
        }
      ).zE;
      zE("webWidget", "show");
      zE("webWidget", "open");
      zE("webWidget:on", "close", () => {
        zE("webWidget", "hide");
      });
    }
  }, []);

  return (
    <Navbar
      logo={logo ?? undefined}
      logoAlt="Brand Logo"
      email={adminDetails?.email}
      cities={filteredCities}
      selectedCity={selectedCity}
      onCityChange={handleCityChange}
      whitelabels={whitelabelList}
      selectedWhitelabel={selectedWhitelabel}
      onWhitelabelChange={handleWhitelabelChange}
      showWhitelabelSelector={effectiveIsSuperAdmin}
      partners={partners}
      selectedPartner={selectedPartner}
      onPartnerChange={handlePartnerChange}
      showPartnerSelector={partnerAccessEnabled && partners.length > 0}
      onLogout={logoutAction}
      onSettingsClick={
        adminDetails?.cityId != null ? undefined : handleSettingsClick
      }
      onLogoClick={handleLogoClick}
      onHelpClick={handleHelpClick}
      onMenuClick={onMenuClick}
      onVersionToggle={onVersionToggle}
      versionLabel={versionLabel}
      onTourClick={onTourClick}
      showCitySelector={effectiveIsCityAdmin}
      backgroundColor={theme?.navBackground}
      textColor={theme?.navText}
    />
  );
};

export default connector(Header);
